import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Building2, ChevronDown, Check, Shield, AlertTriangle, ExternalLink } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const TenantSelector = () => {
  const { user, getAccessToken, demoMode } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMfaWarning, setShowMfaWarning] = useState(false);
  const [pendingTenant, setPendingTenant] = useState(null);
  const [mfaVerified, setMfaVerified] = useState(false);

  // Get the stored selected tenant from localStorage
  const getStoredTenantId = () => {
    return localStorage.getItem("alice_selected_tenant_id");
  };

  // Store the selected tenant
  const storeSelectedTenant = (tenantId) => {
    localStorage.setItem("alice_selected_tenant_id", tenantId);
  };

  // Fetch accessible tenants
  const fetchTenants = useCallback(async () => {
    if (demoMode) {
      setTenants([{
        tenant_id: "demo-tenant",
        display_name: "Demo Organization",
        role: "tenant_admin",
        role_display_name: "Tenant Admin",
        is_home_tenant: true,
        is_external: false,
        mfa_required: false,
        status: "active"
      }]);
      setCurrentTenant({
        tenant_id: "demo-tenant",
        display_name: "Demo Organization",
        is_home_tenant: true
      });
      setLoading(false);
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await axios.get(`${API}/users/accessible-tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const accessibleTenants = response.data.tenants || [];
      setTenants(accessibleTenants);
      setMfaVerified(response.data.mfa_verified);

      // Determine current tenant
      const storedTenantId = getStoredTenantId();
      let selectedTenant = null;

      console.log("[TenantSelector] Stored tenant ID from localStorage:", storedTenantId);
      console.log("[TenantSelector] Accessible tenants:", accessibleTenants.map(t => ({ id: t.tenant_id, name: t.display_name, is_home: t.is_home_tenant })));

      if (storedTenantId) {
        selectedTenant = accessibleTenants.find(t => t.tenant_id === storedTenantId);
        console.log("[TenantSelector] Found tenant matching stored ID:", selectedTenant ? selectedTenant.display_name : "NOT FOUND");
      }

      if (!selectedTenant && accessibleTenants.length > 0) {
        // Default to home tenant or first available - BUT DON'T overwrite if we had a stored value
        selectedTenant = accessibleTenants.find(t => t.is_home_tenant) || accessibleTenants[0];
        console.log("[TenantSelector] No stored tenant found in list, defaulting to:", selectedTenant?.display_name);
        if (selectedTenant && !storedTenantId) {
          // Only store if there was NO previous stored tenant ID
          // This prevents overwriting a valid selection if tenant list is temporarily incomplete
          storeSelectedTenant(selectedTenant.tenant_id);
          console.log("[TenantSelector] Stored default tenant ID (no previous selection)");
        } else if (selectedTenant && storedTenantId) {
          // The stored tenant wasn't found - this might be a permissions issue
          // Store the new default but warn
          console.warn("[TenantSelector] ⚠️ Stored tenant ID not in accessible list! Switching to:", selectedTenant.display_name);
          storeSelectedTenant(selectedTenant.tenant_id);
        }
      }

      setCurrentTenant(selectedTenant);
    } catch (error) {
      console.error("Failed to fetch accessible tenants:", error);
      toast.error("Failed to load organization list");
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, demoMode]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  // Handle tenant switch
  const handleTenantSwitch = async (tenant) => {
    if (tenant.tenant_id === currentTenant?.tenant_id) {
      return; // Already selected
    }

    // Check if MFA is required for this tenant
    if (tenant.is_external && tenant.mfa_required && !mfaVerified) {
      setPendingTenant(tenant);
      setShowMfaWarning(true);
      return;
    }

    // Validate access before switching
    try {
      const token = await getAccessToken();
      await axios.post(
        `${API}/users/validate-tenant-access?tenant_id=${tenant.tenant_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Store and set the new tenant
      storeSelectedTenant(tenant.tenant_id);
      setCurrentTenant(tenant);
      
      toast.success(`Switched to ${tenant.display_name}`);
      
      // Reload the page to refresh all data with new tenant context
      window.location.reload();
    } catch (error) {
      if (error.response?.status === 403) {
        const detail = error.response.data?.detail;
        if (detail?.includes("MFA")) {
          setPendingTenant(tenant);
          setShowMfaWarning(true);
        } else {
          toast.error(`Access denied: ${detail || "You don't have access to this organization"}`);
        }
      } else {
        toast.error("Failed to switch organization");
      }
    }
  };

  // Handle MFA warning dialog
  const handleMfaRedirect = () => {
    // Redirect to Microsoft login with MFA prompt
    // The user will need to re-authenticate with MFA
    toast.info("Please sign in again with multi-factor authentication");
    setShowMfaWarning(false);
    // Could implement a re-login flow here
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm alice-ink-faint">
        <Building2 className="h-4 w-4 animate-pulse" />
        <span>Loading...</span>
      </div>
    );
  }

  // Don't show selector if only one tenant
  if (tenants.length <= 1) {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <Building2 className="h-4 w-4 alice-ink-soft" />
        <span className="text-sm alice-ink truncate max-w-[150px]">
          {currentTenant?.display_name || "My Organization"}
        </span>
      </div>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            className="flex items-center gap-2 px-3 py-2 alice-nav rounded-lg w-full justify-between"
            data-testid="tenant-selector-trigger"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Building2 className="h-4 w-4 alice-ink-soft flex-shrink-0" />
              <span className="text-sm alice-ink truncate">
                {currentTenant?.display_name || "Select Organization"}
              </span>
              {currentTenant?.is_external && (
                <Badge variant="outline" className="text-[10px] px-1 py-0 border-[#006AF2] text-[#006AF2] flex-shrink-0">
                  External
                </Badge>
              )}
            </div>
            <ChevronDown className="h-4 w-4 alice-ink-faint flex-shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent 
          align="start" 
          className="w-[280px] bg-[#0a1929] border-white/10"
          data-testid="tenant-selector-menu"
        >
          <DropdownMenuLabel className="text-gray-400 text-xs">
            Switch Organization
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-white/10" />
          
          {tenants.map((tenant) => (
            <DropdownMenuItem
              key={tenant.tenant_id}
              onClick={() => handleTenantSwitch(tenant)}
              className="flex items-center justify-between gap-2 cursor-pointer hover:bg-white/5 focus:bg-white/5"
              data-testid={`tenant-option-${tenant.tenant_id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className={`h-4 w-4 flex-shrink-0 ${tenant.is_home_tenant ? 'text-cyan-400' : 'text-purple-400'}`} />
                <div className="flex flex-col min-w-0">
                  <span className="text-sm text-white truncate">{tenant.display_name}</span>
                  <span className="text-xs text-gray-500">{tenant.role_display_name}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-1 flex-shrink-0">
                {tenant.is_external && (
                  <ExternalLink className="h-3 w-3 text-purple-400" />
                )}
                {tenant.mfa_required && !mfaVerified && (
                  <Shield className="h-3 w-3 text-orange-400" title="MFA required" />
                )}
                {tenant.tenant_id === currentTenant?.tenant_id && (
                  <Check className="h-4 w-4 text-green-400" />
                )}
              </div>
            </DropdownMenuItem>
          ))}
          
          {!mfaVerified && tenants.some(t => t.is_external && t.mfa_required) && (
            <>
              <DropdownMenuSeparator className="bg-white/10" />
              <div className="px-2 py-1.5 text-xs text-orange-400 flex items-center gap-1">
                <Shield className="h-3 w-3" />
                Some organizations require MFA
              </div>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* MFA Warning Dialog */}
      <Dialog open={showMfaWarning} onOpenChange={setShowMfaWarning}>
        <DialogContent className="bg-[#0a1929] border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <AlertTriangle className="h-5 w-5 text-orange-400" />
              Multi-Factor Authentication Required
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              <strong>{pendingTenant?.display_name}</strong> requires multi-factor authentication 
              for external users. Your current session was not authenticated with MFA.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 text-sm text-orange-200">
            <p>To access this organization, you'll need to:</p>
            <ol className="list-decimal list-inside mt-2 space-y-1">
              <li>Sign out of your current session</li>
              <li>Sign in again using multi-factor authentication</li>
              <li>Return to ALICE and switch to {pendingTenant?.display_name}</li>
            </ol>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowMfaWarning(false)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleMfaRedirect}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
            >
              Sign Out & Re-authenticate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TenantSelector;
